import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import MonthNav from '@/components/feature/MonthNav';
import { useStore } from '@/hooks/useStore';
import type { RetiradaProduto } from '@/mocks/store';

export default function RetiradaProdutosPage() {
  const { produtos, retiradaProdutos, addRetiradaProduto, updateRetiradaProduto, deleteRetiradaProduto } = useStore();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<RetiradaProduto | null>(null);
  const [form, setForm] = useState({ produtoId: '', quantidade: '1', motivo: 'Uso pessoal', data: new Date().toISOString().split('T')[0] });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());

  const produtoSel = produtos.find(p => p.id === form.produtoId);
  // Usa apenas o custo base (sem custoExtra — sacolas etc ficam com a loja)
  const custoUnitario = produtoSel ? produtoSel.custo : 0;
  const custoTotal = custoUnitario * (Number(form.quantidade) || 0);

  const mesPad = String(mes).padStart(2, '0');
  const anoStr = String(ano);

  const filtered = retiradaProdutos.filter(r => {
    if (!r.data) return true;
    const [av, mv] = r.data.split('-');
    return av === anoStr && mv === mesPad;
  });

  const totalCustoFiltrado = filtered.reduce((acc, r) => acc + r.custoTotal, 0);
  const totalUnidadesFiltrado = filtered.reduce((acc, r) => acc + r.quantidade, 0);
  const totalGeralCusto = retiradaProdutos.reduce((acc, r) => acc + r.custoTotal, 0);

  return (
    <AppLayout>
      <TopBar title="Retirada de Produtos" subtitle="Produtos retirados para uso pessoal" />
      <ActionBar>
        <MonthNav mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); }} />
        <button onClick={() => { setEditingItem(null); setForm({ produtoId: '', quantidade: '1', motivo: 'Uso pessoal', data: new Date().toISOString().split('T')[0] }); setShowModal(true); }} className="btn-gold w-full flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap">
          <i className="ri-shirt-line text-base"></i> Retirar Produto
        </button>
      </ActionBar>

      <div className="pt-[200px] md:pt-[200px] p-3 md:p-6 space-y-3 animate-fade-in-up">
        {/* Cards resumo */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Custo Total</p>
            <p className="text-base md:text-xl font-bold font-mono text-orange-400">{filtered.reduce((acc, r) => acc + r.custoTotal, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Unidades</p>
            <p className="text-base md:text-xl font-bold font-mono text-dark-text">{totalUnidadesFiltrado}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Total Geral</p>
            <p className="text-base md:text-xl font-bold font-mono text-yellow-400">{totalGeralCusto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/25 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-information-line text-yellow-400 text-sm"></i></div>
            <p className="text-xs text-dark-muted leading-relaxed">
              Retiradas usam apenas o <strong className="text-yellow-400">custo base do produto</strong> (sem extras como sacolas/embalagens). O valor é lançado automaticamente como despesa e o estoque é descontado.
            </p>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-dark-card border border-dark-border rounded-xl p-10 flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-dark-card2 mb-3"><i className="ri-shirt-line text-2xl text-dark-muted"></i></div>
            <p className="text-sm font-semibold text-dark-text mb-1">Nenhuma retirada neste mês</p>
            <p className="text-xs text-dark-muted">Registre produtos retirados para uso pessoal</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(item => (
              <div key={item.id} className="bg-dark-card border border-dark-border rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-dark-text text-sm truncate">{item.produtoNome}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-dark-card2 text-dark-muted">{item.motivo}</span>
                      <span className="text-[10px] text-dark-muted">{new Date(item.data).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <button onClick={() => { setEditingItem(item); setForm({ produtoId: item.produtoId, quantidade: String(item.quantidade), motivo: item.motivo, data: item.data }); setShowModal(true); }} className="w-7 h-7 flex items-center justify-center rounded-lg text-dark-muted hover:text-neon cursor-pointer transition-colors"><i className="ri-edit-line text-sm"></i></button>
                    <button onClick={() => setConfirmDelete(item.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-dark-muted hover:text-red-400 cursor-pointer transition-colors"><i className="ri-delete-bin-line text-sm"></i></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-dark-card2 rounded-lg p-2 text-center">
                    <p className="text-[9px] text-dark-muted uppercase mb-0.5">Qtd</p>
                    <p className="text-xs font-bold font-mono text-dark-text">{item.quantidade}x</p>
                  </div>
                  <div className="bg-dark-card2 rounded-lg p-2 text-center">
                    <p className="text-[9px] text-dark-muted uppercase mb-0.5">Custo Unit.</p>
                    <p className="text-xs font-bold font-mono text-dark-text">{item.custoUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-2 text-center">
                    <p className="text-[9px] text-dark-muted uppercase mb-0.5">Total Custo</p>
                    <p className="text-xs font-bold font-mono text-orange-400">{item.custoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up max-h-[88vh] overflow-y-auto shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editingItem ? 'Editar Retirada' : 'Retirar Produto'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Produto</label>
                <select className="input-dark" value={form.produtoId} onChange={e => setForm(f => ({ ...f, produtoId: e.target.value }))}>
                  <option value="">Selecione o produto...</option>
                  {produtos.filter(p => p.status === 'ativo' && p.estoque > 0).map(p => (
                    <option key={p.id} value={p.id}>{p.nome} — Estoque: {p.estoque}</option>
                  ))}
                </select>
              </div>
              {produtoSel && (
                <div className="bg-dark-card2 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-dark-muted">Custo unitário</span>
                  <span className="text-sm font-bold font-mono text-orange-400">{custoUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Quantidade</label>
                  <input type="number" className="input-dark" placeholder="1" min="1" max={produtoSel?.estoque} value={form.quantidade} onChange={e => setForm(f => ({ ...f, quantidade: e.target.value }))} />
                  {produtoSel && <p className="text-[10px] text-dark-muted mt-1">Disponível: {produtoSel.estoque}</p>}
                </div>
                <div>
                  <label className="text-xs text-dark-muted mb-1 block font-medium">Motivo</label>
                  <select className="input-dark" value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}>
                    {['Uso pessoal', 'Presente', 'Amostras', 'Brinde', 'Outros'].map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Data</label>
                <input type="date" className="input-dark" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
              </div>
              {custoTotal > 0 && (
                <div className="bg-orange-500/8 border border-orange-500/25 rounded-xl p-3 space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dark-muted">Custo unitário</span>
                    <span className="text-sm font-bold font-mono text-dark-text">{custoUnitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dark-muted">Quantidade</span>
                    <span className="text-sm font-bold font-mono text-dark-text">{form.quantidade}x</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-dark-border pt-1.5">
                    <span className="text-xs font-semibold text-dark-text">Custo total retirada</span>
                    <span className="text-lg font-bold font-mono text-orange-400">{custoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => {
                if (!form.produtoId || !form.quantidade || Number(form.quantidade) < 1) return;
                if (editingItem) {
                  updateRetiradaProduto(editingItem.id, { produtoId: form.produtoId, produtoNome: produtoSel?.nome ?? editingItem.produtoNome, quantidade: Number(form.quantidade), custoUnitario, custoTotal, motivo: form.motivo, data: form.data });
                } else {
                  if (!produtoSel) return;
                  addRetiradaProduto({ produtoId: form.produtoId, produtoNome: produtoSel.nome, quantidade: Number(form.quantidade), custoUnitario, custoTotal, motivo: form.motivo, data: form.data });
                }
                setShowModal(false); setEditingItem(null); setForm({ produtoId: '', quantidade: '1', motivo: 'Uso pessoal', data: new Date().toISOString().split('T')[0] });
              }} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">{editingItem ? 'Salvar' : 'Confirmar Retirada'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/10 mx-auto mb-4"><i className="ri-delete-bin-line text-red-400 text-xl"></i></div>
            <h3 className="text-center text-base font-bold text-dark-text mb-2">Excluir retirada?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteRetiradaProduto(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
